import { useEffect, useState } from 'react';
import { apiRequest } from './client';

// Travel insurance in the API's own format (see /api/insurance in the backend README).

export const searchInsurance = (request, signal) =>
  apiRequest('/api/insurance/search', { body: request, signal });

/** Confirms one plan's price. Returns a reviewId for checkout, valid for 30 minutes. */
export const reviewInsurance = ({ supplierCode, planId, productId }, signal) =>
  apiRequest('/api/insurance/review', { body: { supplierCode, planId, productId }, signal });

/** Guest or signed-in purchase of a reviewed plan; links it to an account by email like flight checkout. */
export const checkoutInsurance = (request, token) =>
  apiRequest('/api/insurance/checkout', { body: request, token });

/** Live status and policy numbers. Guests pass the booking email. */
export const getInsuranceBooking = (supplierBookingId, email, token, signal) =>
  apiRequest('/api/insurance/bookings/details', {
    body: { supplierBookingId, email: email || null },
    token,
    signal,
  });

/** Refund for cancelling the given travellers (all active policies when travellerIds is empty). Requires sign-in. */
export const quoteInsuranceCancellation = (supplierBookingId, travellerIds, token) =>
  apiRequest('/api/insurance/bookings/cancellation/quote', {
    body: { supplierBookingId, travellerIds: travellerIds?.length ? travellerIds : null },
    token,
  });

export const cancelInsurance = (supplierBookingId, cancellationId, token) =>
  apiRequest('/api/insurance/bookings/cancellation', { body: { supplierBookingId, cancellationId }, token });

/** Runs a search whenever the request changes; `retry` searches again. */
export function useInsuranceSearch(request) {
  const key = request ? JSON.stringify(request) : '';
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState({ loading: Boolean(request), data: null, error: '' });

  useEffect(() => {
    if (!key) return undefined;
    const controller = new AbortController();
    setState({ loading: true, data: null, error: '' });
    searchInsurance(JSON.parse(key), controller.signal)
      .then((data) => setState({ loading: false, data, error: '' }))
      .catch((error) => {
        if (error.name === 'AbortError') return;
        setState({ loading: false, data: null, error: error.message || 'We could not load insurance plans.' });
      });
    return () => controller.abort();
  }, [key, attempt]);

  return { ...state, retry: () => setAttempt((n) => n + 1) };
}
